import type { ChildTicketTask } from '@/lib/services/childTicketService'
import type { TicketPart, TicketPartStatus } from '@/types/parentTicket'
import { getSlaStatus } from '@/components/helpdesk/TicketDetail/overview/slaUtils'
import { partIsLinkedToTask } from '@/lib/utils/ticketPartLinkage'
import { badgesFromFormFields, resolveTaskFormFields, shouldUseLegacyFormFields, TASK_FE_SCHEDULE_ENABLED } from './modules/resolveFormFields'
export type TaskFilter = 'all' | 'active' | 'blocked' | 'breached' | 'completed'

/** Final part statuses — procurement is resolved when every linked part is one of these. */
export const PROCUREMENT_FINAL_STATUSES = new Set<TicketPartStatus>(['received', 'not_available'])

export type PartsProcurementCompletion = {
  partsLinked: boolean
  partsComplete: boolean
  unresolvedCount: number
  linkedCount: number
  linkedParts: TicketPart[]
}

/** Linked-parts completion for a parts_procurement task (shared by drawer + list). */
export function getPartsProcurementCompletion(
  taskId: number,
  parts: TicketPart[],
): PartsProcurementCompletion {
  const linkedParts = parts.filter((part) => partIsLinkedToTask(part, taskId))
  const unresolvedCount = linkedParts.filter(
    (part) => !PROCUREMENT_FINAL_STATUSES.has(part.status ?? 'pending'),
  ).length
  const partsLinked = linkedParts.length > 0

  return {
    partsLinked,
    partsComplete: !partsLinked || unresolvedCount === 0,
    unresolvedCount,
    linkedCount: linkedParts.length,
    linkedParts,
  }
}

type ChildTicketStatusCatalog = {
  code: string
  scope?: string | null
  is_terminal?: boolean
}

const STATUS_CATALOG_STORAGE_KEY = 'helpdesk.child_ticket_status_catalog'

function readStatusCatalog(): ChildTicketStatusCatalog[] {
  try {
    const raw = window.localStorage.getItem(STATUS_CATALOG_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function findStatus(code?: string | null): ChildTicketStatusCatalog | undefined {
  if (!code) return undefined
  return readStatusCatalog().find((status) => status.code === code)
}

export const FE_VISIT_SUPERSEDED_STATUS = 'fe_visit_superseded'
export const FE_VISIT_SUPERSEDED_LABEL = 'Reassigned'

export function isFeVisitSuperseded(task: ChildTicketTask): boolean {
  return (
    task.status === FE_VISIT_SUPERSEDED_STATUS ||
    (task.reassigned_to_task_id != null && Number(task.reassigned_to_task_id) > 0)
  )
}

export function resolveTaskStatusLabel(task: ChildTicketTask): string {
  if (isFeVisitSuperseded(task)) {
    return FE_VISIT_SUPERSEDED_LABEL
  }
  return task.status_label?.trim() || task.status?.trim() || 'Unknown'
}

export function isTaskComplete(task: ChildTicketTask): boolean {
  const status = findStatus(task.status)
  return Boolean(task.actual_end) || status?.is_terminal === true
}

export function isTaskSkipped(task: ChildTicketTask): boolean {
  return Boolean(task.skipped)
}

export function isTaskBlocked(task: ChildTicketTask, tasks: ChildTicketTask[]): boolean {
  if (isTaskComplete(task) || !task.depends_on_task_id) return false
  const dep = tasks.find((t) => t.id === task.depends_on_task_id)
  return !dep || !dep.actual_end
}

export function isTaskBreached(task: ChildTicketTask): boolean {
  if (task.actual_end || !task.sla_due_at) return false
  return getSlaStatus(task.sla_due_at).urgency === 'overdue'
}

export function isTaskDueSoon(task: ChildTicketTask): boolean {
  if (task.actual_end || !task.sla_due_at) return false
  return getSlaStatus(task.sla_due_at).urgency === 'due_soon'
}

export function filterTasks(tasks: ChildTicketTask[], filter: TaskFilter): ChildTicketTask[] {
  switch (filter) {
    case 'active':
      return tasks.filter((t) => !isTaskComplete(t))
    case 'blocked':
      return tasks.filter((t) => isTaskBlocked(t, tasks))
    case 'breached':
      return tasks.filter((t) => isTaskBreached(t))
    case 'completed':
      return tasks.filter((t) => isTaskComplete(t))
    default:
      return tasks
  }
}

/** Maps task form_fields or legacy action_type/status to operational panel type. */
export function resolveTaskPanelType(task: ChildTicketTask): 'fe' | 'parts' | 'permit' | 'assets' | null {
  const fields = resolveTaskFormFields(task)
  if (TASK_FE_SCHEDULE_ENABLED && fields.some((f) => f.module === 'core.store' || f.module === 'core.fe_schedule')) {
    return 'fe'
  }
  if (!TASK_FE_SCHEDULE_ENABLED && fields.some((f) => f.module === 'core.store')) {
    return 'fe'
  }
  if (fields.some((f) => f.module === 'core.assets')) return 'assets'
  if (fields.some((f) => f.module === 'core.parts')) return 'parts'
  if (fields.some((f) => f.module === 'core.work_permit')) return 'permit'

  if (!shouldUseLegacyFormFields(task)) {
    return null
  }

  const action = (task.action_type ?? '').trim()

  if (action === 'fe_schedule' && (TASK_FE_SCHEDULE_ENABLED || fields.some((f) => f.module === 'core.store'))) {
    return 'fe'
  }
  if (action === 'fe_visit' || action === 'field_visit') {
    return 'fe'
  }
  if (action === 'parts') return 'parts'
  if (action === 'work_permit') return 'permit'
  if (action === 'assets') return 'assets'
  if (action && action !== 'none') return null

  const status = findStatus(task.status)
  if (status?.scope === 'fe_visit') return 'fe'
  if (status?.scope === 'parts') return 'parts'
  if (status?.scope === 'permit') return 'permit'
  if (status?.scope === 'custom') return 'assets'
  return null
}

/** Short label for task list badges (from form_fields registry or legacy panel). */
export function taskPanelBadgeLabel(task: ChildTicketTask): string | null {
  const labels = taskPanelBadgeLabels(task)
  return labels[0] ?? null
}

/** All badge labels for a task (preview / list). */
export function taskPanelBadgeLabels(task: ChildTicketTask): string[] {
  const fields = resolveTaskFormFields(task)
  const fromFields = badgesFromFormFields(fields)
  if (fromFields.length > 0) return fromFields
  const panel = resolveTaskPanelType(task)
  switch (panel) {
    case 'fe':
      return TASK_FE_SCHEDULE_ENABLED ? ['Store · FE'] : ['Store']
    case 'assets':
      return ['Assets']
    case 'parts':
      return ['Parts']
    case 'permit':
      return ['Permit']
    default:
      return []
  }
}

/** Find first task that hosts a given operational panel. */
export function findTaskByPanelType(
  tasks: ChildTicketTask[],
  panel: 'fe' | 'assets' | 'parts' | 'permit',
): ChildTicketTask | undefined {
  return tasks.find((t) => {
    if (resolveTaskPanelType(t) === panel) return true
    const fields = resolveTaskFormFields(t)
    if (panel === 'fe') {
      return fields.some((f) => f.module === 'core.store' || (TASK_FE_SCHEDULE_ENABLED && f.module === 'core.fe_schedule'))
    }
    if (panel === 'assets') return fields.some((f) => f.module === 'core.assets')
    if (panel === 'parts') return fields.some((f) => f.module === 'core.parts')
    if (panel === 'permit') return fields.some((f) => f.module === 'core.work_permit')
    return false
  })
}

export function formatTaskRelativeTime(task: ChildTicketTask): string {
  if (task.actual_end) {
    const start = task.actual_start ? new Date(task.actual_start).getTime() : null
    const end = new Date(task.actual_end).getTime()
    if (start && !Number.isNaN(start) && !Number.isNaN(end)) {
      const ms = end - start
      const hours = Math.floor(ms / 3600000)
      const mins = Math.floor((ms % 3600000) / 60000)
      if (hours >= 1) return `Took ${hours}h ${mins > 0 ? `${mins}m` : ''}`.trim()
      return `Took ${Math.max(1, mins)}m`
    }
    return 'Completed'
  }

  if (task.sla_due_at) {
    return getSlaStatus(task.sla_due_at).label
  }

  return 'No SLA target'
}

export function computeTaskStats(tasks: ChildTicketTask[]) {
  const total = tasks.length
  const completed = tasks.filter((t) => isTaskComplete(t)).length
  const open = total - completed
  const skipped = tasks.filter((t) => isTaskSkipped(t)).length
  const breached = tasks.filter((t) => isTaskBreached(t)).length
  const dueSoon = tasks.filter((t) => isTaskDueSoon(t)).length
  const overdueTasks = tasks.filter((t) => isTaskBreached(t))
  const bottleneckTeam =
    overdueTasks.length > 0
      ? overdueTasks[0]?.assignee_name ?? 'Unassigned team'
      : null

  return {
    total,
    completed,
    open,
    skipped,
    breached,
    dueSoon,
    bottleneckTeam,
    percent: total > 0 ? Math.round((completed / total) * 100) : 0,
  }
}

export function defaultPipelineView(tasks: ChildTicketTask[]): boolean {
  return tasks.some((t) => t.depends_on_task_id != null || t.workflow_template_step_id != null)
}

export type TaskAgingBucket = 'fresh' | 'aging' | 'old' | 'critical'

export const TASK_AGING_BUCKET_LABELS: Record<TaskAgingBucket, string> = {
  fresh: 'Fresh',
  aging: 'Aging',
  old: 'Old',
  critical: 'Critical',
}

export const TASK_AGING_BUCKET_COLORS: Record<TaskAgingBucket, string> = {
  fresh: 'green',
  aging: 'yellow',
  old: 'orange',
  critical: 'red',
}

/** Mirrors backend ChildTicket::categorizeAgingHours (hours since created_at). */
export function categorizeAgingHours(hours: number): TaskAgingBucket {
  if (hours <= 24) return 'fresh'
  if (hours <= 48) return 'aging'
  if (hours <= 72) return 'old'
  return 'critical'
}

export function computeTaskAgingHours(task: ChildTicketTask): number | null {
  if (!task.created_at || isTaskComplete(task)) return null
  const created = new Date(task.created_at).getTime()
  if (Number.isNaN(created)) return null
  return Math.max(0, Math.floor((Date.now() - created) / 3600000))
}

export function getTaskAgingBucket(task: ChildTicketTask): TaskAgingBucket | null {
  const hours = computeTaskAgingHours(task)
  if (hours === null) return null
  return categorizeAgingHours(hours)
}

export function formatTaskAgeHours(hours: number): string {
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  const rem = hours % 24
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`
}
